import { calculateTotalJourneyBaggageCost } from 'checkout/lib/utils/flights/extras'
import { mergeFees } from 'checkout/lib/utils/payments/fees'
import { excludeNullOrUndefined } from 'checkout/utils'
import { sum } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import { getFlightItems, getFlightItemsView } from 'checkout/selectors/view/flights'
import { VIRGIN_AUSTRALIA } from 'constants/flight'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'

export const getFlightTotals = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (flightItemsWithStatus): App.WithDataStatus<App.Checkout.ItemViewTotals> => {
    const definedItems = flightItemsWithStatus.data.filter(excludeNullOrUndefined)

    const itemTotals: App.Checkout.ItemViewTotals = {
      price: sum(definedItems, itemView => itemView.totals.price || itemView.quotedFare || 0),
      memberPrice: 0,
      value: sum(definedItems, itemView => itemView.totals.value || 0),
      surcharge: 0,
      taxesAndFees: 0,
      otherFees: {
        ...mergeFees(definedItems.map(itemView => itemView.totals.otherFees).filter(excludeNullOrUndefined)),
        flightBaggageTotal: sum(definedItems, itemView => calculateTotalJourneyBaggageCost(itemView)),
      },
      memberValue: 0,
      extraGuestSurcharge: 0,
    }

    return {
      hasRequiredData: flightItemsWithStatus.hasRequiredData,
      data: itemTotals,
    }
  },
)

export const getFlightsDateChangeFees = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (existingOrder, allFlightDetails, postPurchaseMode): App.WithDataStatus<App.Checkout.ItemViewTotals> => {
    const isFlightDateChange = postPurchaseMode === 'flight-change-dates' && !!existingOrder

    if (!isFlightDateChange) {
      return {
        hasRequiredData: true,
        data: {
          price: 0,
          memberPrice: 0,
          value: 0,
          surcharge: 0,
          taxesAndFees: 0,
          memberValue: 0,
          extraGuestSurcharge: 0,
        },
      }
    }

    // Get actual fee data from flight details
    const flightItemId = existingOrder?.flightItems[0]?.id
    const flightDetails = allFlightDetails[flightItemId ?? '']
    const airlineChangeFee = flightDetails?.flight?.flightChangePenalties?.fees.totalAmount || 0
    const luxuryEscapesChangeFee = flightDetails?.flight?.flightChangePenalties?.fees.handlingFee || 0
    const totalChangeFees = airlineChangeFee + luxuryEscapesChangeFee

    return {
      hasRequiredData: !!flightDetails,
      data: {
        price: totalChangeFees,
        memberPrice: 0,
        value: totalChangeFees,
        surcharge: 0,
        taxesAndFees: 0,
        memberValue: 0,
        extraGuestSurcharge: 0,
      },
    }
  },
)

/**
 * Total cost of all flights provided by Virgin Australia
 */
export const getVirginFlightTotals = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (flightItemsViewWithStatus): App.WithDataStatus<App.Checkout.ItemViewTotals> => {
    const definedItems = flightItemsViewWithStatus.data
      .filter(excludeNullOrUndefined)
      .filter(view => view.validatingCarrierName === VIRGIN_AUSTRALIA)

    return {
      hasRequiredData: flightItemsViewWithStatus.hasRequiredData,
      data: {
        price: sum(definedItems, itemView => itemView.totals.price || itemView.quotedFare || 0),
        memberPrice: 0,
        value: sum(definedItems, itemView => itemView.totals.value),
        surcharge: 0,
        taxesAndFees: 0,
        otherFees: {
          ...mergeFees(definedItems.map(itemView => itemView.totals.otherFees).filter(excludeNullOrUndefined)),
          flightBaggageTotal: sum(definedItems, itemView => calculateTotalJourneyBaggageCost(itemView)),
        },
        memberValue: 0,
        extraGuestSurcharge: 0,
      },
    }
  },
)

export const getCartFlightJourneys = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (flightItems, journeys): Array<App.Journey> => {
    // @ts-ignore
    return flightItems.map(item => journeys[item.searchId] as App.Journey).filter(Boolean)
  },
)
